import { buildCsvSchemaLookups, isCsvSchemaHeaderRecognized } from './csvSchemaColumnLookup';
import { detectParentSkuColumn } from './detectParentSkuColumn';
import { isSystemCsvHeader, SYSTEM_CSV_FIELDS } from './importFieldRegistry';
import normalizeMappingName from './normalizeMappingName';

const TORIUT_RECORD_ID_HEADER = normalizeMappingName(SYSTEM_CSV_FIELDS.recordId);
const OPTION_HEADER_RE = /^(.*) \(option\)$/i;
const SCOPED_CUSTOM_AUX_HEADER_RE = /^(.*) \((product|variant)\) (unit|currency|name|title)$/i;
const SCOPED_CUSTOM_HEADER_RE = /^(.*) \((product|variant)\)$/i;

export function parseToriutScopedCustomHeader(columnName) {
  const trimmed = String(columnName ?? '').trim();
  const auxMatch = trimmed.match(SCOPED_CUSTOM_AUX_HEADER_RE);
  if (auxMatch) {
    const title = auxMatch[1].trim();
    if (!title) {
      return { isCustom: false };
    }
    return {
      isCustom: true,
      title,
      scope: auxMatch[2].toLowerCase(),
      part: auxMatch[3].toLowerCase(),
    };
  }

  const match = trimmed.match(SCOPED_CUSTOM_HEADER_RE);
  if (!match) {
    return { isCustom: false };
  }
  const title = match[1].trim();
  if (!title) {
    return { isCustom: false };
  }
  return {
    isCustom: true,
    title,
    scope: match[2].toLowerCase(),
    part: 'value',
  };
}

export function parseProductOptionHeader(columnName) {
  const trimmed = String(columnName ?? '').trim();
  const match = trimmed.match(OPTION_HEADER_RE);
  if (!match) {
    return { isOption: false, name: trimmed };
  }
  const name = match[1].trim();
  if (!name) {
    return { isOption: false, name: trimmed };
  }
  return { isOption: true, name };
}

export function optionNameFromColumn(columnName) {
  return parseProductOptionHeader(columnName).name;
}

export function isToriutImportFile(fileColumns = []) {
  return fileColumns.some(
    (columnName) => normalizeMappingName(columnName) === TORIUT_RECORD_ID_HEADER,
  );
}

export function hasNonEmptyColumnValues(matrix, columnName) {
  if (!Array.isArray(matrix) || matrix.length < 2 || !columnName) {
    return false;
  }

  const headers = matrix[0] || [];
  const colIndex = headers.indexOf(columnName);
  if (colIndex < 0) {
    return false;
  }

  return matrix.slice(1).some((row) => String(row?.[colIndex] ?? '').trim() !== '');
}

export function shouldAutodetectToriutVariants(matrix) {
  const fileColumns = matrix?.[0] || [];
  if (!isToriutImportFile(fileColumns)) {
    return false;
  }

  const parentSkuColumn = detectParentSkuColumn(fileColumns);
  return hasNonEmptyColumnValues(matrix, parentSkuColumn);
}

export async function resolveToriutVariantAutodetect(previewMatrix, loadFullMatrix) {
  const fileColumns = previewMatrix?.[0] || [];
  if (!isToriutImportFile(fileColumns)) {
    return false;
  }
  if (shouldAutodetectToriutVariants(previewMatrix)) {
    return true;
  }
  if (typeof loadFullMatrix !== 'function') {
    return false;
  }
  const fullMatrix = await loadFullMatrix();
  return shouldAutodetectToriutVariants(fullMatrix);
}

export function listUnmappedImportColumns({
  fileColumns = [],
  mappingValues = {},
  auxColumnUsage = {},
  reservedColumns = [],
} = {}) {
  const reserved = new Set(reservedColumns.filter(Boolean));

  return fileColumns.filter((columnName) => {
    if (!columnName) {
      return false;
    }
    if (reserved.has(columnName)) {
      return false;
    }
    if (mappingValues[columnName]) {
      return false;
    }
    if (auxColumnUsage[columnName]) {
      return false;
    }
    return true;
  });
}

export function detectToriutOptionColumns({
  fileColumns = [],
  csvSchemaColumns = [],
  parentSkuColumn = null,
} = {}) {
  if (!csvSchemaColumns.length) {
    return [];
  }

  return fileColumns.filter((columnName) => {
    if (!columnName || columnName === parentSkuColumn) {
      return false;
    }
    if (isSystemCsvHeader(columnName)) {
      return false;
    }
    return parseProductOptionHeader(columnName).isOption;
  });
}

export function isToriutCsvSchemaUnavailable(isToriutFile, csvSchemaColumns = []) {
  return Boolean(isToriutFile) && !(csvSchemaColumns || []).length;
}

export function listUnrecognizedToriutColumns({
  fileColumns = [],
  csvSchemaColumns = [],
  parentSkuColumn = null,
} = {}) {
  if (!isToriutImportFile(fileColumns)) {
    return [];
  }

  // Empty schema cannot tell recognized from added headers. Callers must gate
  // Next/Import with isToriutCsvSchemaUnavailable instead of faking a break.
  if (!csvSchemaColumns.length) {
    return [];
  }

  const { recognizedHeaders } = buildCsvSchemaLookups(csvSchemaColumns);

  return fileColumns.filter((columnName) => {
    if (!columnName || columnName === parentSkuColumn) {
      return false;
    }
    if (isSystemCsvHeader(columnName)) {
      return false;
    }
    if (parseProductOptionHeader(columnName).isOption) {
      return false;
    }
    if (parseToriutScopedCustomHeader(columnName).isCustom) {
      return false;
    }
    return !isCsvSchemaHeaderRecognized(columnName, recognizedHeaders);
  });
}
